import React, { useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { selectCategories, selectCateringCategories, selectIsModifyOrderMode, selectModifyOrderPax } from "../../redux/features/restaurants/restaurantSelectors";
import { transformCategories } from "../../utils/menu";
import Categories from "./categories";
import MenuItemsList from "./menuItemsList";
import CateringMenuList from "./cateringMenuList";
import PaxSelector from "../cateringPackages/PaxSelector";

export const Menu = ({ branchDetails, isCatering = false, activePackageId = null, displayName = null, disabled = false, packageData = null, isModifyOrderMode = false, isAdditionalService = false, additionalServiceCategories = null, additionalServiceItems = null }) => {
  // Get modify order mode from Redux
  const reduxIsModifyOrderMode = useSelector(selectIsModifyOrderMode);
  const reduxModifyOrderPax = useSelector(selectModifyOrderPax);
  
  // Only apply modify order mode if explicitly passed OR Redux state confirms it
  const actualModifyMode = (isModifyOrderMode || reduxIsModifyOrderMode) && 
                          reduxModifyOrderPax !== null;
  
  console.log('🔍 Menu Component - Modify Mode Debug:', {
    'Props: isModifyOrderMode': isModifyOrderMode,
    'Redux: isModifyOrderMode': reduxIsModifyOrderMode,
    'Redux: modifyOrderPax': reduxModifyOrderPax,
    'Calculated: actualModifyMode': actualModifyMode,
    'Will disable PAX': actualModifyMode,
    'isAdditionalService': isAdditionalService
  });
  const navigate = useNavigate();
  const categoriesList = useSelector(selectCategories);
  const cateringCategoriesList = useSelector(selectCateringCategories);
  const [activeCategory, setActiveCategory] = useState("All Menu");
  // const [orderedItems, setOrderedItems] = useState([]);
  // const [subtotal, setSubtotal] = useState(0);
  // const [tax, setTax] = useState(0.05); // 5% tax
  // const [deliveryCharges] = useState(0.5);
  const [searchQuery, setSearchQuery] = useState("");

  // const totalPayable = subtotal + subtotal * tax + deliveryCharges;

  // Use additional service categories if this is an additional service menu
  // Otherwise use catering categories when in catering mode, or regular categories
  const selectedCategoriesList = isAdditionalService 
    ? additionalServiceCategories 
    : (isCatering ? cateringCategoriesList : categoriesList);
  const categories = selectedCategoriesList && transformCategories(selectedCategoriesList);
  
  // Debug logging for categories
  console.log("🔍 Menu component - isCatering:", isCatering);
  console.log("🔍 Menu component - isAdditionalService:", isAdditionalService);
  console.log("🔍 Menu component - additionalServiceCategories prop:", additionalServiceCategories);
  console.log("🔍 Menu component - cateringCategoriesList from Redux:", cateringCategoriesList);
  console.log("🔍 Menu component - selectedCategoriesList:", selectedCategoriesList);
  console.log("🔍 Menu component - selectedCategoriesList length:", selectedCategoriesList?.length);
  console.log("🔍 Menu component - transformed categories:", categories);
  console.log("🔍 Menu component - additionalServiceItems prop:", additionalServiceItems);

  const handleBackClick = () => {
    navigate(-1);
    // Alternative: navigate('/search'); // This will navigate specifically to /search
  };

  return (
    <>
      <div className="sm:w-full p-4 flex flex-col h-full overflow-hidden">
        {/* Header Section */}
        <div className="flex-shrink-0">
          <div className="flex flex-wrap items-center justify-between mb-4">
            <div className="flex items-center gap-4">
              {/* Show back button only for main package, but keep space for alignment */}
              <div className="p-2" style={{ width: '40px', height: '40px' }}>
                {!isAdditionalService && (
                  <button 
                    className="w-full h-full"
                    onClick={handleBackClick}
                  >
                    <img src="/Images/Menu/Bag.png" alt="Menu Bag" className="w-full h-full object-contain" />
                  </button>
                )}
              </div>
              {displayName && (
                <h1 className="sm:text-3xl text-2xl text-amber-100 font-bold">
                  {displayName}
                </h1>
              )}
            </div>
            <div className="flex items-center gap-4 w-full sm:w-auto mt-4 sm:mt-0">
              {isCatering && <PaxSelector disabled={actualModifyMode} selectedPackage={packageData} isAdditionalService={isAdditionalService} />}
              <div className="relative">
                <input
                  type="text"
                  className="w-full sm:w-[235px] h-11 rounded-xl p-2 pl-10 border-2 text-xs text-gray-700 font-bold border-gray-300"
                  placeholder="Search Item"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value.toLowerCase())}
                />
                <img
                  src="/Images/Menu/Icon - Search.png"
                  alt="Search Icon"
                  className="absolute left-4 top-1/2 transform -translate-y-1/2"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Main Content: Categories (Left) + Items (Right) */}
        <div className="flex flex-1 gap-6 min-h-0 mt-4">
          {/* Left Sidebar - Categories */}
          <div className="flex-shrink-0 w-[180px] flex flex-col">
            <h2 className="text-lg font-bold text-gray-800 mb-3">Categories</h2>
            <div className="flex-1 overflow-y-auto pr-2">
              <Categories
                categories={categories}
                activeCategory={activeCategory}
                setActiveCategory={setActiveCategory}
                disabled={disabled}
                isCatering={isCatering}
                packageData={packageData}
                activePackageId={activePackageId}
                isAdditionalService={isAdditionalService}
              />
            </div>
          </div>

          {/* Vertical Divider */}
          <div className="w-px bg-gray-200 flex-shrink-0"></div>

          {/* Right Side - Menu Items */}
          <div className={`flex-1 min-h-0 overflow-y-auto ${disabled ? 'opacity-50 pointer-events-none' : ''}`}>
            {isCatering ? (
              <CateringMenuList 
                activeCategory={activeCategory} 
                searchQuery={searchQuery} 
                activePackageId={activePackageId} 
                disabled={disabled} 
                isModifyOrderMode={actualModifyMode}
                isAdditionalService={isAdditionalService}
                additionalServiceItems={additionalServiceItems}
              />
            ) : (
              <MenuItemsList activeCategory={activeCategory} searchQuery={searchQuery} branchDetails={branchDetails} disabled={disabled}/>
            )}
          </div>
        </div>
      </div>
    </>
  );
};
